import React, { Suspense, useEffect, useState } from 'react';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import config from 'devextreme/core/config';
import { licenseKey } from 'devextreme-license';
import NavigationDrawer from './components/navigation/NavigationDrawer';
import styles from './App.module.css';
import { aiqLogger } from 'services/aiqLogger';
import { useDispatch, useSelector } from 'react-redux';
import * as authSelectors from 'store/authSlice/authSelectors';
import { selectFeatureFlagData } from 'store/featureFlag/featureFlagSelector';
import { featureFlagActions } from 'store/featureFlag/featureFlagSlice';
import useInterceptors from 'hooks/interceptors/useInterceptors';

import { Route, useLocation } from 'react-router-dom';
import Notifier from 'components/UI/Notifications/Notifier';
import CameraStreamWindowPortal from 'components/transaction/components/panels/transaction/VideoWallMode/CompactMode/CameraStreamWindow/CameraStreamWindowPortal';
import PlaybackWindowPortal from 'components/transaction/components/playback/PlaybackWindowPortal';
import StreamPermissionsDialog from 'components/UI/Dialogs/StreamPermissionsDialog/StreamPermissionsDialog';
import { sendLocalStorageSignal, sessionStorageTransferHandler } from 'utils/sessionStorageHandlers';
import { authActions } from 'store/authSlice/authSlice';
import { getKurentoConfig, getCookie } from 'utils/utils';
import { PROFILE } from 'config';
import { SentryRoutes } from 'config/sentry';

import { alertService } from 'services/AlertService';
import { kurentoConfig } from 'services/CameraStream/KurentoRtspSource/KurentoClient/KurentoConfig';
import ProtectedRoute from 'components/protectedRoute/ProtectedRoute';
import { webRtcTelemetry } from 'services/StreamingObservability/WebRtcTelemetryService';

//pages
import Home from 'pages/home';
import NoPage from 'pages/no_page';
import LinearLoader from 'components/UI/Loaders/LinearLoader';
import Redirect from 'pages/Redirect';
import { transactionTableConfig } from 'store/tableControlSlice/transactionTablesConfig';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { beforeUnloadHandler } from 'utils/beforeunloadHandler';
import { registerActivityLifecycleListeners } from 'services/activityTracking/lifecycle';
import { selectIsSharedWorkerDead, selectReconnectToSharedWorker } from 'store/generalSlice/generalSelectors';
import DisconnectedFromSharedWorker from 'components/UI/Notifications/DisconnectedFromSharedWorker';
import { USER_ROLES } from 'utils/constants';

const StreamTest = React.lazy(() => import('pages/StreamTest.jsx'));
const Transaction = React.lazy(() => import('pages/TransactionPage'));
const AllTransactionsPage = React.lazy(() => import('pages/AllTransactionsPage'));
const CameraConfiguration = React.lazy(() => import('pages/CameraConfiguration'));
const DeskConfiguration = React.lazy(() => import('pages/DeskConfiguration'));
const DeskAssignLocation = React.lazy(() => import('pages/DeskAssignLocation/DeskAssignLocation'));
const VinCheck = React.lazy(() => import('pages/VinCheck'));
const IssueTracker = React.lazy(() => import('pages/IssueTracker/IssueTracker'));
const RelayActivityPage = React.lazy(() => import('pages/RelayActivityPage/RelayActivityPage'));

config({ licenseKey });

function App() {
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector(authSelectors.selectUser);
    const featureFlagData = useSelector(selectFeatureFlagData);
    const isUserDataRequested = useSelector(authSelectors.selectIsUserDataRequested);
    const [kurentoConfigPresent, setKurentoConfigPresent] = useState(false);
    const reconnectToSharedWorker = useSelector(selectReconnectToSharedWorker);
    const isSharedWorkerDead = useSelector(selectIsSharedWorkerDead);
    useInterceptors();

    if (!sessionStorage.getItem('session_active')) {
        localStorage.removeItem('videoWallCameraBrightnessContrast');
        localStorage.removeItem('transactionCameraBrightnessContrast');

        sessionStorage.setItem('session_active', 'true');
        localStorage.setItem('videoWallCameraBrightnessContrast', '{}');
        localStorage.setItem('transactionCameraBrightnessContrast', '{}');
    }

    const hasUserRole = (...role) => {
        for (let userRole of role) if (isAllowed(userRole)) return true;
        return false;
    };
    const isAllowed = (role) => user?.roles?.includes(role) || user?.roles?.includes(USER_ROLES.ADMIN);

    function formatPageTitle(path, brandName = 'AIQ') {
        if (!path || typeof path !== 'string') return brandName;
        const cleanPath = path.replace(/^\//, '');
        if (!cleanPath) return brandName;
        const words = cleanPath.split('-');
        const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        const pageTitle = capitalizedWords.join(' ');

        return `${pageTitle} ${pageTitle && '|'} ${brandName}`;
    }

    useEffect(() => {
        document.title = formatPageTitle(location.pathname);
    }, [location]);

    useEffect(() => {
        window.addEventListener('storage', sessionStorageTransferHandler);
        window.addEventListener('beforeunload', beforeUnloadHandler);
        // BEAST-13036. The only `visibilitychange` listener in the app, owned by the activity
        // tracking module; page unload rides on the existing `beforeUnloadHandler` above.
        const unregisterActivityLifecycle = registerActivityLifecycleListeners();
        let cookieLasta;
        let cookieSso;
        let cookieAiq;
        let cookieAlert;
        let cookieGu;

        switch (PROFILE) {
            case 'development':
                cookieLasta = getCookie('lastaD');
                cookieSso = getCookie('ssoD');
                cookieAiq = getCookie('aiqD');
                cookieAlert = getCookie('alertD');
                cookieGu = getCookie('guD');
                break;
            case 'staging':
                cookieLasta = getCookie('lastaS');
                cookieSso = getCookie('ssoS');
                cookieAiq = getCookie('aiqS');
                cookieAlert = getCookie('alertS');
                cookieGu = getCookie('guS');
                break;
            case 'production':
                cookieLasta = getCookie('lastaP');
                cookieSso = getCookie('ssoP');
                cookieAiq = getCookie('aiqP');
                cookieAlert = getCookie('alertP');
                cookieGu = getCookie('guP');
                break;
            default:
                cookieLasta = getCookie('lasta');
                cookieSso = getCookie('sso');
                cookieAiq = getCookie('aiq');
                cookieAlert = getCookie('alert');
                cookieGu = getCookie('gu');
        }

        if (cookieSso || cookieAiq || cookieAlert || cookieGu || cookieLasta) {
            alertService.activate();
            return;
        } else {
            switch (PROFILE) {
                case 'development':
                    document.cookie = 'aiqD=AlertsON; path=/; ';
                    break;
                case 'staging':
                    document.cookie = 'aiqS=AlertsON; path=/; domain=.birdseyesecurity.com; ';
                    break;
                case 'production':
                    document.cookie = 'aiqP=AlertsON; path=/; domain=.birdseyesecurity.com; ';
                    break;
                default:
                    document.cookie = 'aiq=AlertsON; path=/; ';
            }
            alertService.activate();
        }

        return () => {
            alertService.deactivate();
            aiqLogger.removeEventListeners();
            window.removeEventListener('beforeunload', beforeUnloadHandler);
            window.removeEventListener('storage', sessionStorageTransferHandler);
            unregisterActivityLifecycle();
        };
    }, []);

    useEffect(() => {
        user && dispatch(transactionActions.registerSharedWorker());
    }, [dispatch, user]);

    useEffect(() => {
        dispatch(featureFlagActions.getFeatureFlagData());
    }, [dispatch]);

    useEffect(() => {
        webRtcTelemetry.setRuntimeEnabled(Boolean(featureFlagData?.streamingWebRtcTelemetryEnabled));
    }, [featureFlagData?.streamingWebRtcTelemetryEnabled]);

    useEffect(() => {
        if (!user) {
            !sessionStorage?.length && sendLocalStorageSignal('AIQ_GetSessionStorage');
        } else if (!kurentoConfigPresent) {
            getKurentoConfig().then((isConfigurationSet) => {
                if (isConfigurationSet) {
                    setKurentoConfigPresent(true);
                }
            });
        }

        return () => {
            if (kurentoConfigPresent) {
                kurentoConfig.removeConfig();
                setKurentoConfigPresent(false);
            }
        };
    }, [dispatch, user, kurentoConfigPresent]);

    useEffect(() => {
        !user && dispatch(authActions.getUserData());
    }, [user, dispatch]);

    if (!isUserDataRequested)
        return (
            <div className={styles['cheking-permission']}>
                <p>Checking permissions...</p>
                <LinearLoader containerStyles={{ position: 'relative' }} />
            </div>
        );

    return (
        <>
            {user && <NavigationDrawer user={user} />}
            {/* App level, not in DesksPanel: the heartbeat runs on every page, so a dead SharedWorker must be visible on every page. */}
            {isSharedWorkerDead && <DisconnectedFromSharedWorker />}
            <Suspense fallback={<LinearLoader containerStyles={{ width: '70%', top: '30vh', left: '15vw' }} />}>
                <SentryRoutes>
                    <Route path="/redirect" element={<Redirect user={user} />} />
                    <Route element={<ProtectedRoute isAllowed={!!user || hasUserRole(USER_ROLES.TEAM_LEADER)} redirectPath="/redirect" />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/streams" element={kurentoConfigPresent && <StreamTest />} />
                        <Route path="/all-desks" element={kurentoConfigPresent && <Transaction key={reconnectToSharedWorker} />} />
                        <Route
                            path="/all-events"
                            element={
                                <AllTransactionsPage
                                    tableId={transactionTableConfig.allLocationsTransactions.tableId}
                                    tableConfigName={transactionTableConfig.allLocationsTransactions.configName}
                                    title="All Events"
                                    showDateTime
                                />
                            }
                        />
                        <Route
                            path="/manual-departures"
                            element={
                                <AllTransactionsPage
                                    tableId={transactionTableConfig.allLocationsAnnouncements.tableId}
                                    tableConfigName={transactionTableConfig.allLocationsAnnouncements.configName}
                                    title="Manual Departures"
                                />
                            }
                        />

                        <Route path="/vin-processing" element={kurentoConfigPresent && <VinCheck />} />
                        <Route path="/issue-reports" element={kurentoConfigPresent && <IssueTracker />} />
                        <Route path="/relay-logs" element={kurentoConfigPresent && <RelayActivityPage />} />
                    </Route>

                    <Route element={<ProtectedRoute isAllowed={hasUserRole(USER_ROLES.ADMIN, USER_ROLES.TEAM_LEADER)} />}>
                        <Route path="/saturation" element={<DeskAssignLocation />} />
                        <Route path="/camera-layouts" element={<CameraConfiguration />} />
                    </Route>

                    <Route element={<ProtectedRoute isAllowed={hasUserRole(USER_ROLES.ADMIN)} />}>
                        <Route path="/settings" element={<DeskConfiguration />} />
                    </Route>

                    <Route path="*" element={<NoPage />} />
                </SentryRoutes>
            </Suspense>
            <Notifier />
            <StreamPermissionsDialog />
            <CameraStreamWindowPortal />
            <PlaybackWindowPortal />
        </>
    );
}

export default App;
